import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { getAppointmentAnalytics, getSingleAppointment } from "../../store/appointment/appointmentActions";
import { CountdownTimer } from "../../components/course/counterdown";
import { getImage } from "../../utils";
import moment from "moment";
import { FiArrowLeft, FiActivity, FiCalendar } from "react-icons/fi";
import { RescheduleEvent } from "../../components/course/reschedule";
import { CancelEvent } from "../../components/course/cancel";
import { ApproveEvent } from "../../components/course/approve";
import { ReportEvent } from "../../components/course/report";
import { AppointmentHistory } from "./history";
import { Toaster } from "react-hot-toast";

const formatTimestamp = (value) => {
    if (!value) return "N/A";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : "N/A";
};

const formatDuration = (seconds) => {
    if (seconds == null || Number.isNaN(Number(seconds))) return "N/A";
    const total = Number(seconds);
    const mins = Math.floor(total / 60);
    const secs = Math.floor(total % 60);
    return `${mins}m ${secs}s`;
};

const CallAnalyticsPanel = ({ appointmentId }) => {
    const dispatch = useDispatch();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!appointmentId) return;
        setLoading(true);
        dispatch(getAppointmentAnalytics(appointmentId))
            .unwrap()
            .then((data) => {
                setAnalytics(data);
                setLoading(false);
            })
            .catch((err) => {
                setError(err?.error || "Failed to load analytics");
                setLoading(false);
            });
    }, [appointmentId, dispatch]);

    if (loading) {
        return (
            <div className="mt-5 border border-slate-200 rounded-xl p-4 bg-slate-50">
                <div className="flex items-center gap-2 text-nt-grey">
                    <FiActivity size={16} />
                    <h4 className="font-semibold text-nt-blue">Call Analytics</h4>
                </div>
                <p className="text-nt-grey text-sm mt-2">Loading analytics…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-5 border border-red/20 rounded-xl p-4 bg-red/5">
                <div className="flex items-center gap-2">
                    <FiActivity size={16} className="text-red" />
                    <h4 className="font-semibold text-nt-blue">Call Analytics</h4>
                </div>
                <p className="text-red text-sm mt-1">{error}</p>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="mt-5 border border-slate-200 rounded-xl p-4 bg-slate-50">
                <div className="flex items-center gap-2">
                    <FiActivity size={16} className="text-nt-grey" />
                    <h4 className="font-semibold text-nt-blue">Call Analytics</h4>
                </div>
                <p className="text-nt-grey text-sm mt-1">No call analytics available for this appointment.</p>
            </div>
        );
    }

    const members = Array.isArray(analytics.members) ? analytics.members : [];
    const status = analytics.status || "pending";
    const joinedCount = members.filter((m) => m.joined !== false && (m.joinTime || m.joined)).length;

    return (
        <div className="mt-5 border border-slate-200 rounded-xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-2">
                    <FiActivity size={16} className="text-nt-blue" />
                    <h4 className="font-semibold text-nt-blue">Call Analytics</h4>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${
                    status === "success" ? "bg-green/10 text-green" :
                    status === "failed" ? "bg-red/10 text-red" :
                    "bg-amber-100 text-amber-700"
                }`}>{status}</span>
            </div>

            <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-nt-grey">Channel</p>
                        <p className="text-xs font-medium text-nt-blue mt-0.5 truncate">{analytics.channel || "N/A"}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-nt-grey">Session Duration</p>
                        <p className="text-sm font-semibold text-nt-blue">{formatDuration(analytics?.summary?.durationSeconds)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-nt-grey">Participants Joined</p>
                        <p className="text-sm font-semibold text-nt-blue">{joinedCount} / {members.length}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-nt-grey">Fetched At</p>
                        <p className="text-xs font-medium text-nt-blue">{formatTimestamp(analytics.fetchedAt)}</p>
                    </div>
                </div>

                {status === "failed" && analytics.errorMessage && (
                    <div className="bg-red/5 border border-red/20 rounded-lg p-3 mb-4 text-sm text-red">
                        {analytics.errorMessage}
                    </div>
                )}

                {status === "pending" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-700">
                        Analytics collection is in progress. Data will be available shortly after the session ends.
                    </div>
                )}

                {members.length > 0 ? (
                    <div className="space-y-2">
                        <p className="text-xs font-semibold text-nt-grey uppercase tracking-wide">Participants</p>
                        {members.map((member, index) => {
                            const didJoin = member.joined !== false && (member.joinTime || member.joined);
                            return (
                                <div key={`${member.uid}-${index}`} className="border border-slate-200 rounded-lg p-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                                                member.role === "student" ? "bg-blue-100 text-blue-700" :
                                                member.role === "tutor" ? "bg-purple-100 text-purple-700" :
                                                "bg-slate-100 text-slate-600"
                                            }`}>{member.role}</span>
                                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                                                didJoin ? "bg-green/10 text-green" : "bg-red/10 text-red"
                                            }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${didJoin ? "bg-green" : "bg-red"}`} />
                                                {didJoin ? "Joined" : "Did not join"}
                                            </span>
                                        </div>
                                        <span className="text-xs text-nt-grey">UID: {member.uid}</span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 text-xs text-nt-grey">
                                        <div><span className="text-nt-blue font-medium">Joined: </span>{formatTimestamp(member.joinTime)}</div>
                                        <div><span className="text-nt-blue font-medium">Left: </span>{formatTimestamp(member.leaveTime)}</div>
                                        <div><span className="text-nt-blue font-medium">Duration: </span>{formatDuration(member.durationSeconds)}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-sm text-nt-grey">No per-participant data available yet.</p>
                )}
            </div>
        </div>
    );
};

const AppointmentDetails = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { currentProfile } = useSelector(state => state.profile)
    const [loading, setLoading] = useState(true);
    const [appointment, setAppointment] = useState(null);
    const [isReschedule, setIsReschedule] = useState(false);
    const [isCancel, setIsCancel] = useState(false);
    const [isApprove, setIsApprove] = useState(false);
    const [isReport, setIsReport] = useState(false);

    const [isSendingReview, setIsSendingReview] = useState(false);
    const [reviewResult, setReviewResult] = useState(false);
    useEffect(() => {
        handleInit();
    }, [])

    const handleInit = () => {
        setIsReschedule(false)
        dispatch(getSingleAppointment(id))
            .unwrap()
            .then((v) => {
                // console.log(v)
                setAppointment(v)
                setLoading(false)
            })
    }

    const handleReview = (result) => {
        setIsSendingReview(true)
        setReviewResult(result)
    }

    const dispatchReview = () => {
        
    }

    const Info = () => {
        return <div className="flex flex-col md:flex-row justify-between items-center p-4">
            <div className="flex gap-4 items-center">
                <img src={getImage(appointment.avatar, "AVATAR")} className="w-14 object-cover h-14 rounded-full" />
                <div className="">
                    <h4 className="text-nt-blue capitalize">{appointment.fullname}</h4>
                    <span className="text-nt-grey text-sm">Date: {moment(appointment.date).utc().format("YYYY-MM-DD HH:mm")}</span>
                </div>
            </div>
            <div className="flex flex-row-reverse gap-1">
                <StatusTag />
                <h3 className="text-nt-blue mt-1 text-right">{appointment.price} €</h3>
            </div>
        </div>
    }

    const Pending = () => {
        return <div className="">
            <Info />
            <div>
                <CountdownTimer target={moment(appointment.dateUtc).parseZone()} text="Course Start In" />
            </div>
            {(!isReschedule && !isCancel) && <div className="flex justify-center gap-5 mb-5">
                <button onClick={() => setIsReschedule(true)} className="w-32 py-2 bg-nt-blue rounded text-white">Reschedule</button>
                <button onClick={() => setIsCancel(true)} className="w-32 py-2 bg-nt-red rounded text-white">Cancel</button>
            </div>}
            {
                isReschedule && <RescheduleEvent refreshParent={() => handleInit()} setIsReschedule={(v) => setIsReschedule(v)} event={appointment} />
            }
            {
                isCancel && <CancelEvent refreshParent={() => handleInit()} setIsCancel={(v) => setIsCancel(v)} event={appointment} />
            }
        </div>
    }

    const PendingPayment = () => {
        return <div className="">
            <Info />
            <div>
                <CountdownTimer text="Payment Due In" target={moment(appointment.dateUtc).parseZone().add(50, "minutes").add(2, "day")} />
            </div>
            {(!isApprove && !isReport) && <div className="flex justify-center gap-5 mb-5">
                {(currentProfile.role && currentProfile.role == 3) ? <button className="w-40 py-2 bg-nt-blue rounded text-white" onClick={() => setIsApprove(true)}>Approve Payment</button> : ""}
                <button className="w-40 py-2 bg-nt-red rounded text-white" onClick={() => setIsReport(true)}>Report Issue</button>
            </div>}
            {
                isApprove && <ApproveEvent refreshParent={() => handleInit()} setIsApprove={(v) => setIsApprove(v)} event={appointment} />
            }
            {
                isReport && <ReportEvent refreshParent={() => handleInit()} setIsReport={(v) => setIsReport(v)} event={appointment} />
            }
        </div>
    }

    const Done = () => {
        return <div className="">
            <Info />
        </div>
    }

    const Canceled = ({by}) => {
        return <div className="">
            <Info />
            <h4 className="text-nt-grey text-center">Canceled By {by}</h4>
        </div>
    }

    const UnderReview = () => {
        return <div className="">
            <Info />
            <h4 className="text-nt-grey text-center">Payment Under review</h4>
            {currentProfile?.role == 1 ?
                <div className="mt-5 pt-3 border-t-2">
                    <ReviewAction />
                </div> 
            : null}
        </div>
    }

    const ReviewAction = () => {
        return !isSendingReview ? <div className="flex justify-center gap-5">
            <button className="text-white bg-nt-blue py-2 px-4 rounded-md" onClick={() => handleReview(true)}>Pay Tutor</button>
            <button className="text-white bg-nt-red py-2 px-4 rounded-md" onClick={() => handleReview(false)}>Refund student</button>
        </div> : <div className="">
            <p className="text-nt-blue text-center mb-3">{reviewResult ? "Are you sure to pay tutor ?" : "Are you sure to refund student ?"}</p>
            <div className="justify-center flex gap-5">
                <button onClick={() => dispatchReview()} className="text-white bg-nt-blue py-2 px-4 rounded-md">Yes</button>
                <button onClick={() => setIsSendingReview(false)} className="text-nt-blue bg-nt-grey-light py-2 px-4 rounded-md">Cancel</button>
            </div>
        </div>
    }

    const PaymentRejected = () => {
        return <div className="">
            <Info />
            <h4 className="text-nt-grey text-center">Payment Rejected</h4>
        </div>
    }

    const StatusTag = () => {
        const status = appointment.status;
        if (status === -1) {
            return <span className={`bg-nt-blue text-white px-2 rounded py-1`}>Pending</span>
        } else if (status === 0) {
            return <span className={`bg-nt-blue text-white px-2 rounded py-1`}>Pending Payment</span>
        } else if (status === 1) {
            return <span className={`bg-green text-white px-2 rounded py-1`}>Done</span>
        } else if (status === 2) {
            return <span className={`bg-red text-white px-2 rounded py-1`}>Canceled</span>
        } else if (status === 3) {
            return <span className={`bg-green opacity-70 text-white px-2 rounded py-1`}>Under Review</span>
        } else if (status === 4) {
            return <span className={`bg-red opacity-70 text-white px-2 rounded py-1`}>Payment Rejected</span>
        }
    }
    const navigate = useNavigate()
    const Details = () => {
        let globalStatus = null;
        // console.log(appointment.cancel?.by, currentProfile._id, appointment.cancel?.by== currentProfile._id)
        switch (appointment.status) {
            case -1:
                globalStatus = <Pending />;
                break;
            case 0:
                globalStatus = <PendingPayment />;
                break;
            case 1:
                globalStatus = <Done />;
                break;
            case 2:
                globalStatus = <Canceled by={
                    appointment.cancel?.by == currentProfile._id ? "you" : appointment.otherName 
                } />;
                break;
            case 3:
                globalStatus = <UnderReview />;
                break;
            case 4:
                globalStatus = <PaymentRejected />;
                break;
        }

        return (
            <>
                <button className="flex items-center no-underline gap-2 text-nt-grey" onClick={() => navigate(-1)}>
                    <FiArrowLeft />Back
                </button>

                {globalStatus}
                <CallAnalyticsPanel appointmentId={appointment._id} />
                <AppointmentHistory otherName={appointment.otherName} history={[
                    ...(appointment.report && appointment.report.by ? [{ ...appointment.report, type: "report" }] : []),
                    ...(appointment.cancel ? [{ ...appointment.cancel, type: "cancel" }] : []),
                    ...(appointment.approve ? [{ ...appointment.approve, type: "approve" }] : []),
                    ...(appointment.reschedule ? [...appointment.reschedule.map(v => ({ ...v, type: "reschedule" }))] : [])
                ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
                />
            </>
        )
        // if (status === -1) {
        //     return <Pending />
        // } else if (status === 0) {
        //     return <span className={`bg-nt-blue text-white px-2 rounded py-1`}>Pending Payment</span>
        // } else if (status === 1) {
        //     return <span className={`bg-green text-white px-2 rounded py-1`}>Done</span>
        // } else if (status === 2) {
        //     return <span className={`bg-red text-white px-2 rounded py-1`}>Canceled</span>
        // } else if (status === 3) {
        //     return <span className={`bg-green opacity-70 text-white px-2 rounded py-1`}>Under Review</span>
        // } else if (status === 4) {
        //     return <span className={`bg-red opacity-70 text-white px-2 rounded py-1`}>Payment Rejected</span>
        // }
        // if(status === -1){
        //     return <span className={`bg-nt-blue text-white px-2 rounded py-1`}>Pending</span>
        // }else if(status === 0){
        //     return <span className={`bg-nt-blue text-white px-2 rounded py-1`}>Pending Payment</span>
        // }else if(status === 1){
        //     return <span className={`bg-green text-white px-2 rounded py-1`}>Done</span>
        // }else if(status === 2){
        //     return <span className={`bg-red text-white px-2 rounded py-1`}>Canceled</span>
        // }else if(status === 3){
        //     return <span className={`bg-green opacity-70 text-white px-2 rounded py-1`}>Under Review</span>
        // }else if(status === 4){
        //     return <span className={`bg-red opacity-70 text-white px-2 rounded py-1`}>Payment Rejected</span>
        // }
    }
    return (
        <div className="mx-auto lg:w-2/3 xl:min-w-[740px]">
            <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-nt-blue/10 text-nt-blue">
                        <FiCalendar size={18} />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-nt-blue">Appointment details</h2>
                        <p className="text-sm text-nt-grey">Status, actions, and history for this booking</p>
                    </div>
                </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                {!loading ? (
                    <>
                        <Toaster />
                        <Details status={appointment.status} />
                    </>
                ) : null}
            </div>
        </div>
    );
}
export { AppointmentDetails }